import React, { useState, useEffect, useMemo } from 'react';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import { useTranslation } from '../i18n/LanguageContext';

// ── Yardımcı ──────────────────────────────────────────────────
function fmt(n, d = 4) { return n == null ? '—' : parseFloat(n).toFixed(d); }
function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })
       + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}
function holdingMin(t) {
  if (!t.created_at || !t.closed_at) return null;
  return Math.round((new Date(t.closed_at) - new Date(t.created_at)) / 60000);
}

// ── SVG Kümülatif P&L Grafiği ─────────────────────────────────
function CumPnlChart({ trades }) {
  const { t } = useTranslation();
  const closed = trades.filter(t => t.status === 'closed' && t.pnl != null);
  if (closed.length === 0) return <div className="rp-chart-empty">{t('reportModal.noData')}</div>;

  const W = 600, H = 160, PAD = { t: 16, r: 16, b: 28, l: 48 };
  const iW = W - PAD.l - PAD.r;
  const iH = H - PAD.t - PAD.b;

  let cum = 0;
  const points = closed.map((t, i) => {
    cum += parseFloat(t.pnl);
    return { i, v: parseFloat(cum.toFixed(4)) };
  });

  const vals  = points.map(p => p.v);
  const minV  = Math.min(0, ...vals);
  const maxV  = Math.max(0, ...vals);
  const range = maxV - minV || 1;

  const sx = i => PAD.l + (i / Math.max(points.length - 1, 1)) * iW;
  const sy = v => PAD.t + iH - ((v - minV) / range) * iH;

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(p.i).toFixed(1)},${sy(p.v).toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${sx(points.length - 1).toFixed(1)},${sy(0).toFixed(1)} L${sx(0).toFixed(1)},${sy(0).toFixed(1)} Z`;
  const lastV = vals[vals.length - 1] || 0;

  const yLabels = [minV, (minV + maxV) / 2, maxV].map(v => ({ v, y: sy(v) }));
  const xStep   = Math.max(1, Math.ceil(points.length / 6));
  const xLabels = points.filter((_, i) => i % xStep === 0 || i === points.length - 1);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {/* Grid */}
      {yLabels.map(({ y }, i) => (
        <line key={i} x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth="1" />
      ))}
      {/* Zero line */}
      <line x1={PAD.l} x2={W - PAD.r} y1={sy(0)} y2={sy(0)} stroke="#e2e8f0" strokeWidth="1.5" strokeDasharray="4,3" />
      {/* Area */}
      <path d={areaD} fill={lastV >= 0 ? 'rgba(16,185,129,.10)' : 'rgba(239,68,68,.10)'} />
      {/* Line */}
      <path d={pathD} fill="none" stroke={lastV >= 0 ? '#10b981' : '#ef4444'} strokeWidth="2" strokeLinejoin="round" />
      {/* Y labels */}
      {yLabels.map(({ v, y }, i) => (
        <text key={i} x={PAD.l - 6} y={y + 4} textAnchor="end" fontSize="9" fill="#94a3b8">
          {v.toFixed(2)}
        </text>
      ))}
      {/* X labels */}
      {xLabels.map((p, i) => (
        <text key={i} x={sx(p.i)} y={H - 4} textAnchor="middle" fontSize="9" fill="#94a3b8">
          {p.i + 1}
        </text>
      ))}
    </svg>
  );
}

// ── P&L Dağılım Çubuğu ───────────────────────────────────────
function PnlBar({ label, value, max, color }) {
  const pct = max === 0 ? 0 : Math.abs(value / max) * 100;
  return (
    <div className="rp-bar-row">
      <span className="rp-bar-label">{label}</span>
      <div className="rp-bar-track">
        <div className="rp-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="rp-bar-val" style={{ color }}>{value >= 0 ? '+' : ''}{value.toFixed(2)}</span>
    </div>
  );
}

// ── Ana Bileşen ───────────────────────────────────────────────
export default function ReportModal({ token, onClose }) {
  const { t } = useTranslation();
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();
  const [tab,       setTab]       = useState('history');
  useTrackPageView(`Raporlar - ${tab === 'history' ? 'Islem Gecmisi' : 'Analiz Raporu'}`);
  const [allTrades, setAllTrades] = useState([]);
  const [loading,   setLoading]   = useState(true);

  // Filtreler
  const [fSymbol,   setFSymbol]   = useState('');
  const [fSide,     setFSide]     = useState('');
  const [fStatus,   setFStatus]   = useState('closed');
  const [fSignal,   setFSignal]   = useState('');
  const [fFrom,     setFFrom]     = useState('');
  const [fTo,       setFTo]       = useState('');
  const [fTest,     setFTest]     = useState('real'); // 'real' | 'test' | 'all'
  const [sortCol,   setSortCol]   = useState('created_at');
  const [sortDir,   setSortDir]   = useState('desc');

  useEffect(() => {
    fetch('/api/trades?limit=2000', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setAllTrades(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  // Filtreli işlemler
  const filtered = useMemo(() => {
    let rows = [...allTrades];
    if (fTest === 'real') rows = rows.filter(t => !t.is_test);
    if (fTest === 'test') rows = rows.filter(t =>  t.is_test);
    if (fSymbol)  rows = rows.filter(t => t.symbol?.toLowerCase().includes(fSymbol.toLowerCase()));
    if (fSide)    rows = rows.filter(t => t.side === fSide);
    if (fStatus)  rows = rows.filter(t => t.status === fStatus);
    if (fSignal)  rows = rows.filter(t => t.signal_type?.toLowerCase().includes(fSignal.toLowerCase()));
    if (fFrom)    rows = rows.filter(t => new Date(t.created_at) >= new Date(fFrom));
    if (fTo)      rows = rows.filter(t => new Date(t.created_at) <= new Date(fTo + 'T23:59:59'));

    rows.sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (sortCol === 'pnl' || sortCol === 'fee' || sortCol === 'leverage' || sortCol === 'margin') { va = parseFloat(va); vb = parseFloat(vb); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [allTrades, fTest, fSymbol, fSide, fStatus, fSignal, fFrom, fTo, sortCol, sortDir]);

  const sort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const SortIcon = ({ col }) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // ── Analitik Hesaplar ─────────────────────────────────────
  const analytics = useMemo(() => {
    const closed = filtered.filter(t => t.status === 'closed' && t.pnl != null);
    const wins   = closed.filter(t => parseFloat(t.pnl) > 0);
    const losses = closed.filter(t => parseFloat(t.pnl) <= 0);

    const totalPnl  = closed.reduce((s, t) => s + parseFloat(t.pnl), 0);
    const totalFee  = closed.reduce((s, t) => s + parseFloat(t.fee || 0), 0);
    const grossWin  = wins.reduce((s, t) => s + parseFloat(t.pnl), 0);
    const grossLoss = Math.abs(losses.reduce((s, t) => s + parseFloat(t.pnl), 0));

    const bySymbol = {};
    closed.forEach(t => {
      bySymbol[t.symbol] = (bySymbol[t.symbol] || 0) + parseFloat(t.pnl);
    });

    const longPnl  = closed.filter(t => t.side === 'long').reduce((s, t) => s + parseFloat(t.pnl), 0);
    const shortPnl = closed.filter(t => t.side === 'short').reduce((s, t) => s + parseFloat(t.pnl), 0);

    const holdings = closed.filter(t => holdingMin(t) != null).map(t => holdingMin(t));
    const avgHold  = holdings.length ? Math.round(holdings.reduce((a, b) => a + b, 0) / holdings.length) : null;

    const best  = closed.reduce((b, t) => parseFloat(t.pnl) > parseFloat(b?.pnl || -Infinity) ? t : b, null);
    const worst = closed.reduce((w, t) => parseFloat(t.pnl) < parseFloat(w?.pnl || Infinity) ? t : w, null);

    return {
      count: closed.length, wins: wins.length, losses: losses.length,
      winRate: closed.length ? (wins.length / closed.length * 100) : 0,
      totalPnl, totalFee, netPnl: totalPnl - Math.abs(totalFee),
      grossWin, grossLoss,
      profitFactor: grossLoss === 0 ? (grossWin > 0 ? Infinity : 0) : grossWin / grossLoss,
      avgWin:  wins.length   ? grossWin  / wins.length   : 0,
      avgLoss: losses.length ? grossLoss / losses.length : 0,
      bySymbol, longPnl, shortPnl, avgHold, best, worst,
    };
  }, [filtered]);

  const symbols  = [...new Set(allTrades.map(t => t.symbol))].sort();
  const maxAbsSym = Math.max(1, ...Object.values(analytics.bySymbol).map(v => Math.abs(v)));

  return (
    <div className="log-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rp-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>

        {/* Başlık */}
        <div className="log-header" onMouseDown={handleHeaderMouseDown}>
          <div className="log-title-row">
            <span className="log-title">{t('reportModal.title')}</span>
            <span className="log-count">{t('reportModal.tradeCount', { n: filtered.length })}</span>
          </div>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>

        {/* Sekmeler */}
        <div className="rp-tabs">
          <button className={`rp-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
            {t('reportModal.tabHistory')}
          </button>
          <button className={`rp-tab ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>
            {t('reportModal.tabAnalytics')}
          </button>
        </div>

        {/* Filtreler */}
        <div className="rp-filters">
          <select className="rp-select" value={fSymbol} onChange={e => setFSymbol(e.target.value)}>
            <option value="">{t('reportModal.allPairs')}</option>
            {symbols.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="rp-select" value={fSide} onChange={e => setFSide(e.target.value)}>
            <option value="">{t('reportModal.allSides')}</option>
            <option value="long">Long</option>
            <option value="short">Short</option>
          </select>
          <select className="rp-select" value={fStatus} onChange={e => setFStatus(e.target.value)}>
            <option value="">{t('reportModal.allStatuses')}</option>
            <option value="closed">{t('reportModal.closed')}</option>
            <option value="open">{t('reportModal.open')}</option>
          </select>
          <input className="rp-input" placeholder={t('reportModal.signalPlaceholder')} value={fSignal} onChange={e => setFSignal(e.target.value)} />
          <input className="rp-input" type="date" value={fFrom} onChange={e => setFFrom(e.target.value)} />
          <input className="rp-input" type="date" value={fTo}   onChange={e => setFTo(e.target.value)} />
          <select className="rp-select" value={fTest} onChange={e => setFTest(e.target.value)}>
            <option value="real">{t('reportModal.real')}</option>
            <option value="test">{t('reportModal.test')}</option>
            <option value="all">{t('reportModal.all')}</option>
          </select>
          <button className="rp-clear" onClick={() => { setFSymbol(''); setFSide(''); setFStatus('closed'); setFSignal(''); setFFrom(''); setFTo(''); setFTest('real'); }}>
            {t('reportModal.reset')}
          </button>
        </div>

        {/* İçerik */}
        <div className="rp-body">
          {loading ? (
            <div className="log-empty">{t('reportModal.loading')}</div>
          ) : tab === 'history' ? (

            // ── SEKME 1: İşlem Geçmişi ─────────────────────
            <>
              {/* Özet istatistik */}
              <div className="rp-summary">
                <div className="rp-stat">
                  <span className="rp-stat-label">{t('reportModal.statTrades')}</span>
                  <span className="rp-stat-val">{analytics.count}</span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">{t('reportModal.statWinRate')}</span>
                  <span className="rp-stat-val" style={{ color: analytics.winRate >= 50 ? '#10b981' : '#ef4444' }}>
                    %{analytics.winRate.toFixed(1)}
                  </span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">{t('reportModal.statTotalPnl')}</span>
                  <span className="rp-stat-val" style={{ color: analytics.totalPnl >= 0 ? '#10b981' : '#ef4444' }}>
                    {analytics.totalPnl >= 0 ? '+' : ''}{analytics.totalPnl.toFixed(2)} $
                  </span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">{t('reportModal.statTotalFee')}</span>
                  <span className="rp-stat-val" style={{ color: '#ef4444' }}>
                    -{Math.abs(analytics.totalFee).toFixed(4)} $
                  </span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">{t('reportModal.statProfitFactor')}</span>
                  <span className="rp-stat-val" style={{ color: analytics.profitFactor >= 1 ? '#10b981' : '#ef4444' }}>
                    {analytics.profitFactor === Infinity ? '∞' : analytics.profitFactor.toFixed(2)}
                  </span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">{t('reportModal.statAvgDuration')}</span>
                  <span className="rp-stat-val">{analytics.avgHold != null ? t('reportModal.minutesShort', { n: analytics.avgHold }) : '—'}</span>
                </div>
              </div>

              {/* Tablo */}
              {filtered.length === 0 ? (
                <div className="log-empty">{t('reportModal.noFilterMatch')}</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="trade-table">
                    <thead>
                      <tr>
                        <th onClick={() => sort('symbol')}    style={{ cursor:'pointer' }}>{t('reportModal.colSymbol')} <SortIcon col="symbol" /></th>
                        <th onClick={() => sort('side')}      style={{ cursor:'pointer' }}>{t('reportModal.colSide')} <SortIcon col="side" /></th>
                        <th onClick={() => sort('entry_price')} style={{ cursor:'pointer' }}>{t('reportModal.colEntry')} <SortIcon col="entry_price" /></th>
                        <th>{t('reportModal.colExit')}</th>
                        <th>{t('reportModal.colSl')}</th><th>{t('reportModal.colTp')}</th>
                        <th onClick={() => sort('pnl')}      style={{ cursor:'pointer' }}>P&amp;L <SortIcon col="pnl" /></th>
                        <th onClick={() => sort('fee')}      style={{ cursor:'pointer' }}>{t('reportModal.colFee')} <SortIcon col="fee" /></th>
                        <th onClick={() => sort('margin')}   style={{ cursor:'pointer' }}>{t('reportModal.colMargin')} <SortIcon col="margin" /></th>
                        <th onClick={() => sort('leverage')} style={{ cursor:'pointer' }}>{t('reportModal.colLeverage')} <SortIcon col="leverage" /></th>
                        <th>{t('reportModal.colSignal')}</th>
                        <th onClick={() => sort('status')} style={{ cursor:'pointer' }}>{t('reportModal.colStatus')} <SortIcon col="status" /></th>
                        <th onClick={() => sort('created_at')} style={{ cursor:'pointer' }}>{t('reportModal.colDate')} <SortIcon col="created_at" /></th>
                        <th>{t('reportModal.colDuration')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(t => {
                        const pnl  = t.pnl != null ? parseFloat(t.pnl) : null;
                        const mins = holdingMin(t);
                        return (
                          <tr key={t.id}>
                            <td style={{ fontWeight: 700 }}>
                              {t.symbol}
                              {t.is_test && (
                                <span style={{
                                  marginLeft: 5, fontSize: 9, fontWeight: 700,
                                  background: 'rgba(161,97,210,0.15)', color: '#a161d2',
                                  border: '1px solid rgba(161,97,210,0.4)',
                                  borderRadius: 3, padding: '1px 4px', verticalAlign: 'middle',
                                }}>TEST</span>
                              )}
                            </td>
                            <td>
                              <span className={`position-side side-${t.side}`}
                                    style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, display: 'inline-block' }}>
                                {t.side?.toUpperCase()}
                              </span>
                            </td>
                            <td>{fmt(t.entry_price)}</td>
                            <td>{fmt(t.exit_price)}</td>
                            <td style={{ color: 'var(--red)' }}>{fmt(t.sl_price)}</td>
                            <td style={{ color: 'var(--accent)' }}>{fmt(t.tp_price)}</td>
                            <td className={pnl == null ? '' : pnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>
                              {pnl == null ? '—' : `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}`}
                            </td>
                            <td style={{ color: 'var(--muted)', fontSize: 12 }}>
                              {t.fee != null ? parseFloat(t.fee).toFixed(4) : '—'}
                            </td>
                            <td style={{ color: 'var(--muted)' }}>{t.margin != null ? `${parseFloat(t.margin)} $` : '—'}</td>
                            <td>{t.leverage}x</td>
                            <td style={{ color: 'var(--muted)', textTransform: 'capitalize' }}>{t.signal_type}</td>
                            <td><span className={`status-chip status-${t.status}`}>{t.status}</span></td>
                            <td style={{ color: 'var(--muted)' }}>{fmtDate(t.created_at)}</td>
                            <td style={{ color: 'var(--muted)', fontSize: 12 }}>
                              {mins != null ? (mins < 60 ? t('reportModal.minutesShort', { n: mins }) : t('reportModal.hoursMinutesShort', { h: Math.floor(mins/60), m: mins%60 })) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>

          ) : (

            // ── SEKME 2: Analiz Raporu ──────────────────────
            <div className="rp-analytics">

              {/* Metrik Kartlar */}
              <div className="rp-metrics">
                {[
                  { l: t('reportModal.metricTotalTrades'),    v: analytics.count,                       c: '#1e293b'  },
                  { l: t('reportModal.metricWins'),         v: analytics.wins,                         c: '#10b981'  },
                  { l: t('reportModal.metricLosses'),        v: analytics.losses,                       c: '#ef4444'  },
                  { l: t('reportModal.metricWinRate'),   v: `%${analytics.winRate.toFixed(1)}`,    c: analytics.winRate >= 50 ? '#10b981' : '#ef4444' },
                  { l: t('reportModal.metricNetPnl'),     v: `${analytics.netPnl >= 0 ? '+' : ''}${analytics.netPnl.toFixed(2)}`, c: analytics.netPnl >= 0 ? '#10b981' : '#ef4444' },
                  { l: t('reportModal.metricProfitFactor'),   v: analytics.profitFactor === Infinity ? '∞' : analytics.profitFactor.toFixed(2), c: analytics.profitFactor >= 1 ? '#10b981' : '#ef4444' },
                  { l: t('reportModal.metricAvgWin'), v: `+${analytics.avgWin.toFixed(2)}`,     c: '#10b981'  },
                  { l: t('reportModal.metricAvgLoss'),  v: `-${analytics.avgLoss.toFixed(2)}`,     c: '#ef4444'  },
                  { l: t('reportModal.metricAvgDuration'),       v: analytics.avgHold != null ? t('reportModal.minutesShort', { n: analytics.avgHold }) : '—', c: '#64748b' },
                  { l: t('reportModal.metricTotalFee'),   v: `-${Math.abs(analytics.totalFee).toFixed(4)}`, c: '#f59e0b' },
                ].map(({ l, v, c }) => (
                  <div key={l} className="rp-metric-card">
                    <span className="rp-metric-label">{l}</span>
                    <span className="rp-metric-val" style={{ color: c }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Kümülatif P&L */}
              <div className="rp-chart-block">
                <div className="rp-chart-title">{t('reportModal.cumPnlTitle')}</div>
                <CumPnlChart trades={filtered} />
              </div>

              {/* Long / Short Karşılaştırma */}
              <div className="rp-two-col">
                <div className="rp-chart-block">
                  <div className="rp-chart-title">{t('reportModal.sideBreakdownTitle')}</div>
                  <PnlBar label="Long"  value={analytics.longPnl}  max={Math.max(Math.abs(analytics.longPnl), Math.abs(analytics.shortPnl), 1)} color="#10b981" />
                  <PnlBar label="Short" value={analytics.shortPnl} max={Math.max(Math.abs(analytics.longPnl), Math.abs(analytics.shortPnl), 1)} color="#3b82f6" />
                  <div style={{ marginTop: 14 }}>
                    {analytics.best && (
                      <div className="rp-extremes">
                        <span className="rp-ext-label">{t('reportModal.best')}</span>
                        <span className="rp-ext-sym">{analytics.best.symbol}</span>
                        <span className="pnl-pos">+{parseFloat(analytics.best.pnl).toFixed(2)}$</span>
                        <span className="rp-ext-date">{fmtDate(analytics.best.created_at)}</span>
                      </div>
                    )}
                    {analytics.worst && (
                      <div className="rp-extremes">
                        <span className="rp-ext-label">{t('reportModal.worst')}</span>
                        <span className="rp-ext-sym">{analytics.worst.symbol}</span>
                        <span className="pnl-neg">{parseFloat(analytics.worst.pnl).toFixed(2)}$</span>
                        <span className="rp-ext-date">{fmtDate(analytics.worst.created_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rp-chart-block">
                  <div className="rp-chart-title">{t('reportModal.symbolBreakdownTitle')}</div>
                  {Object.entries(analytics.bySymbol).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])).map(([sym, val]) => (
                    <PnlBar key={sym} label={sym} value={val} max={maxAbsSym} color={val >= 0 ? '#10b981' : '#ef4444'} />
                  ))}
                  {Object.keys(analytics.bySymbol).length === 0 && (
                    <div className="log-empty" style={{ padding: '20px 0' }}>{t('reportModal.noData')}</div>
                  )}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
